import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useClientes } from '../hooks/useClientes';
import { useToast } from '../hooks/useToast';
import { PedidosNuevosAPI } from '../services/pedidosNuevosService';
import { clientesAPI } from '../services/clientesAPI';
import apiClient from '../services/api';
import ConfirmarPedidoDiaModal from '../components/modals/ConfirmarPedidoDiaModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import {
  ChefHat,
  CheckCircle,
  Clock,
  AlertCircle,
  ArrowLeft,
} from 'lucide-react';
import type {
  EstructuraArmarPedido,
  EstadisticasPedidos,
} from '../types/pedidosNuevos';
import { useMinutasHistoricas } from '../hooks/useMinutasHistoricas';
import BuscadorMinutasHistoricasAcordeon from '../components/minutas/BuscadorMinutasHistoricasAcordeon';
import BannerBorradoresPendientes from '../components/pedidos/BannerBorradoresPendientes';
import GrillaSemanal from '../components/pedidos/GrillaSemanal';
import VistaSegmentadaDiaNoche from '../components/pedidos/VistaSegmentadaDiaNoche';
import AyudaContextual from '../components/AyudaContextual';

/**
 * Función helper para formatear el nombre de la minuta
 * Elimina la parte "T00:00:00.000Z" de las fechas ISO
 */
const formatearNombreMinuta = (nombre: string): string => {
  if (!nombre) return '';
  // Elimina el patrón completo de fechas ISO: YYYY-MM-DDTHH:MM:SS.sssZ
  // Ejemplo: "2025-09-29T03:00:00.000Z al 2025-10-01T03:00:00.000Z" -> ""
  let nombreLimpio = nombre.replace(
    /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?/g,
    ''
  );

  // Elimina "al" solitario y paréntesis vacíos
  nombreLimpio = nombreLimpio.replace(/\s+al\s+/g, ' ');
  nombreLimpio = nombreLimpio.replace(/\(\s*\)/g, '');

  // Limpia espacios múltiples y trim
  nombreLimpio = nombreLimpio.replace(/\s+/g, ' ').trim();

  return nombreLimpio;
};

/**
 * Página para armar pedidos basados en las nuevas minutas
 */
const ArmarPedido: React.FC = () => {
  const { user } = useAuth();
  const { success: toastSuccess, error: toastError } = useToast();

  // Estados principales
  const [clienteId, setClienteId] = useState<number | null>(null);
  // Nombre de la ficha del cliente. Se muestra en vez del nombre del USUARIO:
  // son cosas distintas, y mostrar el del usuario ocultaba que la pantalla
  // estaba cargada con la ficha equivocada.
  const [nombreFicha, setNombreFicha] = useState<string | null>(null);
  const [sinFichaVinculada, setSinFichaVinculada] = useState(false);
  const [minutaId, setMinutaId] = useState<number | null>(null);
  // PASO 5 (2026-06-11) — multi-sede: operación destino del pedido.
  //   Al cambiar de cliente, se cargan sus operaciones activas (GET
  //   /api/clientes/:id enriquecido) y se autoselecciona la principal.
  //   Si el cliente tiene >1 operación, el dropdown queda visible para
  //   permitir cambiar entre ellas (cambia el menú filtrado y el
  //   despacho destino).
  interface OperacionLite {
    id: number;
    nombre: string;
    es_principal: boolean;
  }
  const [operacionesCliente, setOperacionesCliente] = useState<OperacionLite[]>(
    []
  );
  const [operacionId, setOperacionId] = useState<number | null>(null);
  const [cargandoOperaciones, setCargandoOperaciones] = useState(false);
  const [estructura, setEstructura] = useState<EstructuraArmarPedido | null>(
    null
  );
  const [estadisticas, setEstadisticas] = useState<EstadisticasPedidos | null>(
    null
  );

  // Estados de carga
  const [cargandoEstructura, setCargandoEstructura] = useState(false);
  const [guardandoDias, setGuardandoDias] = useState<Set<string>>(new Set());
  const [confirmandoPedido, setConfirmandoPedido] = useState(false);

  // Rediseño "Carta de menú" (2026-06-12) + vista semanal estilo
  // QuickTaste (2026-06-15).
  //
  // Toggle público: 'carta' | 'semanal'.
  //   - 'carta'   = vista actual por día (CartaDeMenu, intacta).
  //   - 'semanal' = grilla categorias × dias (GrillaSemanal, nueva).
  //
  // En pantallas chicas (width < 768) forzamos 'carta' aunque el toggle
  // diga 'semanal' — la grilla 7×N no es usable en celular.
  //
  // Persistencia: sessionStorage por sesión del navegador.
  //
  // El estado (`cantidadesPorDia`) y los handlers son los mismos —
  // cambiar de vista no pierde lo tipeado. Un solo Confirmar.
  type VistaMode = 'carta' | 'semanal';
  const VISTA_STORAGE_KEY = 'armarPedido.vistaMode';
  const leerVistaPref = (): VistaMode => {
    try {
      const v = sessionStorage.getItem(VISTA_STORAGE_KEY);
      return v === 'semanal' ? 'semanal' : 'carta';
    } catch {
      return 'carta';
    }
  };
  const [vistaMode, setVistaModeRaw] = useState<VistaMode>(leerVistaPref);
  const setVistaMode = useCallback((v: VistaMode) => {
    setVistaModeRaw(v);
    try {
      sessionStorage.setItem(VISTA_STORAGE_KEY, v);
    } catch {
      /* sessionStorage puede fallar en modos privados/iframes — ok */
    }
  }, []);

  // Detección viewport mobile (< md = 768px tailwind). Se actualiza en
  // resize; mientras < 768 forzamos render carta independientemente del
  // toggle.
  const [isDesktop, setIsDesktop] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true
  );
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const vistaEfectiva: VistaMode = isDesktop ? vistaMode : 'carta';

  const [diaActivoCarta, setDiaActivoCarta] = useState<string | null>(null);
  // TIPO_PLATO_BEBIDA_ID es la constante del dominio (id=11 en categorias_generales).
  const TIPO_PLATO_BEBIDA_ID = 11;

  // Estados para modales
  const [modalConfirmarDia, setModalConfirmarDia] = useState<{
    isOpen: boolean;
    fecha: string;
    nombreDia: string;
    pedidoId: number | null;
  }>({
    isOpen: false,
    fecha: '',
    nombreDia: '',
    pedidoId: null,
  });

  const [modalConfirm, setModalConfirm] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'warning' | 'danger' | 'info';
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: () => {},
  });

  // Estados para cantidades. Antes la key interna era `recetaId`, lo
  // que rompía cuando la misma receta estaba asignada a múltiples
  // tipos del mismo día (todos los inputs colisionaban en un slot
  // único). Ahora la key es la celda física (tipoPlatoId + filaOrden),
  // que es siempre única dentro de un día. Mapa:
  //   fecha → claveCantidad(tipoPlatoId, filaOrden) → cantidad
  // Al armar el payload se agrupa por recetaId (ver detallesAgrupados).
  const [cantidadesPorDia, setCantidadesPorDia] = useState<
    Map<string, Map<string, number>>
  >(new Map());

  // Estado para rastrear días con cambios sin guardar
  const [diasConCambios, setDiasConCambios] = useState<Set<string>>(new Set());

  // Key para el state de cantidades (no incluye fecha — fecha es la
  // key externa del Map padre).
  const claveCantidad = (tipoPlatoId: number, filaOrden: number) =>
    `${tipoPlatoId}-${filaOrden}`;

  // Key con segmento — usada solo en la vista día/noche cuando la
  // operación tiene flag_pedido_segmentado=1. Compatible con
  // claveCantidad: las keys legacy nunca colisionan porque no terminan
  // en `-dia` ni `-noche`.
  const claveCantidadSeg = (
    tipoPlatoId: number,
    filaOrden: number,
    // Fase 6-bebidas (2026-07-01): también acepta 'transversal' (usado por
    // las bebidas). El sufijo del string queda inequívoco.
    segmento: 'dia' | 'noche' | 'transversal'
  ) => `${tipoPlatoId}-${filaOrden}-${segmento}`;

  // Total de porciones por día calculado en tiempo real desde
  // cantidadesPorDia. Se usa para:
  //   (a) bloquear "Confirmar" cuando el pedido del día está vacío (#5)
  //   (b) calcular recetasSinCantidad para el widget del header (#6)
  // Sumar todas las claves del map interno del día. En modo segmentado
  // los inputs viven bajo claves con sufijo -dia/-noche/-transversal;
  // en modo legacy bajo la clave sin sufijo. Sumamos SOLO las claves
  // que correspondan al flag activo para no doble-contar (cargarEstructura
  // hidrata AMBAS variantes cuando el flag está prendido).
  const totalPorDia = useMemo(() => {
    const flagAct = !!estructura?.operacion?.flagPedidoSegmentado;
    const map = new Map<string, number>();
    cantidadesPorDia.forEach((inner, fecha) => {
      let total = 0;
      inner.forEach((valor, key) => {
        const esSegmentada =
          key.endsWith('-dia') ||
          key.endsWith('-noche') ||
          key.endsWith('-transversal');
        if (flagAct ? esSegmentada : !esSegmentada) total += valor;
      });
      map.set(fecha, total);
    });
    return map;
  }, [cantidadesPorDia, estructura]);

  // Recetas SIN cantidad en tiempo real (#6). Cuenta, para cada slot
  // celda con receta, si su cantidad total (sumando segmentos si aplica)
  // es 0. Reacciona a cada input.
  const recetasSinCantidadReactivo = useMemo(() => {
    if (!estructura) return 0;
    const flagAct = !!estructura.operacion?.flagPedidoSegmentado;
    let count = 0;
    for (const fila of estructura.matriz) {
      for (const celda of fila.dias) {
        if (!celda.receta) continue;
        const inner = cantidadesPorDia.get(celda.fecha);
        let cantidad = 0;
        if (flagAct) {
          const segs = ['dia', 'noche', 'transversal'] as const;
          for (const seg of segs) {
            const k = `${fila.tipoPlato.id}-${fila.tipoPlato.filaOrden}-${seg}`;
            cantidad += inner?.get(k) ?? 0;
          }
        } else {
          const k = `${fila.tipoPlato.id}-${fila.tipoPlato.filaOrden}`;
          cantidad = inner?.get(k) ?? 0;
        }
        if (cantidad === 0) count++;
      }
    }
    return count;
  }, [estructura, cantidadesPorDia]);

  // Está activa la vista día/noche?  true cuando el endpoint indica
  // que la operación destino tiene flag_pedido_segmentado=1.
  const flagSegmentado = !!estructura?.operacion?.flagPedidoSegmentado;

  // Agrupa las cantidades por recetaId para un día — necesario porque
  // pedido_detalles tiene UNIQUE(pedido_id, receta_id), o sea el
  // backend acepta UN solo detalle por receta. Si la misma receta
  // aparece en varias celdas del mismo día (caso raro pero posible),
  // se suman las cantidades. Resultado: lista canónica de detalles
  // listos para enviar al endpoint o mostrar en el modal.
  const detallesAgrupadosDia = (fecha: string) => {
    if (!estructura) return [];
    const cantidadesDia = cantidadesPorDia.get(fecha);
    if (!cantidadesDia) return [];

    // En modo segmentado, una misma receta puede aparecer dos veces
    // (día y noche) — la key del agrupado es (recetaId, segmento).
    // En modo legacy (flag apagado) sigue siendo recetaId puro.
    type AggKey = string;
    type RolDetalle = 'unico' | 'fondo' | 'guarnicion';
    const porReceta = new Map<
      AggKey,
      {
        recetaId: number;
        cantidad: number;
        nombre: string;
        recetaGuarnicion: { id: number; nombre?: string } | null;
        segmento?: 'dia' | 'noche' | 'transversal';
        // Rol del registro principal. Si hay recetaGuarnicion → 'fondo',
        // si no → 'unico'. La guarnición se emite aparte en el guardar
        // con rol='guarnicion'.
        rol: RolDetalle;
        // tipo_plato_id del slot en la minuta. Espejo de
        // minutas_detalladas.tipo_plato_id. Necesario para que
        // sembrarPorDefecto aplique exclusiones por tipo de plato.
        tipoPlatoId: number;
      }
    >();

    if (flagSegmentado) {
      const segmentos: Array<'dia' | 'noche'> = ['dia', 'noche'];
      for (const fila of estructura.matriz) {
        const tp = fila.tipoPlato;
        // Fase 6-bebidas (2026-07-01): BEBIDA usa segmento='transversal'
        // (aplica al día completo, no a día vs noche). El resto sigue con
        // dia/noche según su segmento base o si es dual.
        const esBebida = tp.id === TIPO_PLATO_BEBIDA_ID;
        const segmentosTipo: Array<'dia' | 'noche' | 'transversal'> = esBebida
          ? ['transversal']
          : tp.dual
            ? segmentos
            : tp.segmento === 'dia' || tp.segmento === 'noche'
              ? [tp.segmento]
              : [];
        for (const seg of segmentosTipo) {
          for (const celda of fila.dias) {
            if (celda.fecha !== fecha || !celda.receta) continue;
            const k = claveCantidadSeg(tp.id, tp.filaOrden, seg);
            const cantidad = cantidadesDia.get(k) || 0;
            if (cantidad <= 0) continue;
            const aggKey = `${celda.receta.id}|${seg}`;
            const previo = porReceta.get(aggKey);
            const recetaGuarnicion =
              (celda.receta as { recetaGuarnicion?: { id: number; nombre?: string } })
                .recetaGuarnicion ?? null;
            if (previo) {
              previo.cantidad += cantidad;
            } else {
              porReceta.set(aggKey, {
                recetaId: celda.receta.id,
                cantidad,
                nombre: celda.receta.nombre,
                recetaGuarnicion,
                segmento: seg,
                rol: recetaGuarnicion ? 'fondo' : 'unico',
                tipoPlatoId: tp.id,
              });
            }
          }
        }
      }
    } else {
      // Modo legacy: agrupa por recetaId sin segmento (= NULL en BD).
      for (const fila of estructura.matriz) {
        for (const celda of fila.dias) {
          if (celda.fecha !== fecha || !celda.receta) continue;
          const k = claveCantidad(fila.tipoPlato.id, fila.tipoPlato.filaOrden);
          const cantidad = cantidadesDia.get(k) || 0;
          if (cantidad <= 0) continue;
          const id = celda.receta.id;
          const aggKey = String(id);
          const previo = porReceta.get(aggKey);
          const recetaGuarnicion =
            (celda.receta as { recetaGuarnicion?: { id: number; nombre?: string } })
              .recetaGuarnicion ?? null;
          if (previo) {
            previo.cantidad += cantidad;
          } else {
            porReceta.set(aggKey, {
              recetaId: id,
              cantidad,
              nombre: celda.receta.nombre,
              recetaGuarnicion,
              rol: recetaGuarnicion ? 'fondo' : 'unico',
              tipoPlatoId: fila.tipoPlato.id,
            });
          }
        }
      }
    }

    return Array.from(porReceta.values()).map((data) => ({
      recetaId: data.recetaId,
      cantidad: data.cantidad,
      nombre: data.nombre,
      recetaGuarnicion: data.recetaGuarnicion,
      segmento: data.segmento,
      rol: data.rol,
      tipoPlatoId: data.tipoPlatoId,
    }));
  };

  // Hook para obtener clientes (solo para admin). Pasamos limit alto
  // para evitar el LIMIT 10 default del backend que oculta clientes con
  // contratos amplios como METSO / POLPAICO.
  const esAdmin = user?.rol === 'admin' || user?.rol === 'administrador';
  // Sólo el admin elige cliente; con los demás roles ni siquiera se pide la
  // lista, porque `GET /clientes` exige `clientes.ver` y el rol cliente no lo
  // tiene: pedirla daría 403 y un toast de error por datos que no se usan.
  const { clientes } = useClientes({
    limit: 500,
    activo: true,
    enabled: esAdmin,
  });

  // Minutas activas — son las del dropdown principal. Antes el filtro
  // era `vigentes=true` (fecha_fin >= hoy AND fecha_inicio <= hoy+30d),
  // pero en operación real una minuta semanal puede consultarse el
  // mismo lunes inmediatamente despues de su cierre para armar un
  // pedido tardio, o reabrirse para visualizar / corregir cuando aun
  // se esta en el ciclo operativo. Cambiamos a listar todas las
  // activas y dejamos que la pantalla muestre lo que cargue
  // (orden cronologico).
  const [minutasActivas, setMinutasActivas] = useState<any[]>([]);
  // Trigger para que el banner de borradores pendientes recargue
  // su endpoint cuando se confirma un pedido (un item desaparece).
  const [refreshBanner, setRefreshBanner] = useState(0);
  // Acordeón "Buscar minutas anteriores": hook compartido con Minutas.
  // En Armar Pedido sólo nos interesan minutas ACTIVAS (los clientes no
  // piden sobre borradores ni canceladas/completadas).
  const historicasHook = useMinutasHistoricas({ estado: 'activa' });


  // Cargar minutas activas al montar
  useEffect(() => {
    cargarMinutasActivas();
  }, []);

  const cargarMinutasActivas = async () => {
    try {
      const response = await apiClient.get('/minutas-nuevas', {
        params: {
          estado: 'activa',
          limit: 50,
        },
      });

      if (response.data.success) {
        setMinutasActivas(response.data.data || []);
      }
    } catch (error) {
      console.error('Error cargando minutas vigentes:', error);
      toastError('Error al cargar las minutas vigentes');
    }
  };

  // Cargar la ficha del cliente: operaciones (PASO 5 multi-sede) y nombre.
  //
  // El origen de la ficha depende del rol, y no es un detalle cosmético:
  //
  // - Admin: la elige en el selector, y la lee por id con `clientes.ver`.
  // - Cliente: no tiene permiso sobre el módulo y no hay selector. La ficha
  //   se resuelve por su propio vínculo (`clientes.usuario_id`) vía /mi-ficha.
  //
  // Antes esta pantalla daba por sentado que la ficha del cliente llevaba el
  // mismo id que su usuario (`setClienteId(user.id)`). Es falso: en QA el
  // usuario 9 está vinculado a la ficha 12 (METSO CHILE) pero la ficha 9 es
  // ICL CATODOS. Cada cliente venía armando pedidos contra las operaciones de
  // otra empresa. `clienteId` ahora se toma del id que devuelve la ficha.
  useEffect(() => {
    let cancelado = false;
    if (esAdmin && !clienteId) {
      setOperacionesCliente([]);
      setOperacionId(null);
      return;
    }
    if (!esAdmin && !user?.id) return;
    // El cliente resuelve su ficha UNA vez. Sin este corte el efecto se
    // redispara al fijar `clienteId` con el id que acaba de traer, y la
    // pantalla pide /mi-ficha dos veces en cada carga.
    if (!esAdmin && clienteId) return;
    setCargandoOperaciones(true);
    (esAdmin ? clientesAPI.obtener(clienteId as number) : clientesAPI.miFicha())
      .then((resp) => {
        if (cancelado) return;
        const detalle = resp?.data as {
          id?: number;
          nombre?: string;
          operaciones?: Array<{
            id: number;
            nombre: string;
            es_principal: boolean;
            activa?: boolean;
          }>;
        } | null;
        if (!esAdmin && detalle?.id) {
          setClienteId(detalle.id);
          setNombreFicha(detalle.nombre ?? null);
        }
        const ops: OperacionLite[] = (detalle?.operaciones ?? [])
          .filter((o) => o.activa !== false)
          .map((o) => ({
            id: o.id,
            nombre: o.nombre,
            es_principal: Boolean(o.es_principal),
          }));
        setOperacionesCliente(ops);
        const principal = ops.find((o) => o.es_principal) ?? ops[0] ?? null;
        setOperacionId(principal ? principal.id : null);
      })
      .catch((err) => {
        if (cancelado) return;
        console.error('Error cargando la ficha del cliente:', err);
        setOperacionesCliente([]);
        setOperacionId(null);
        // 404 = el usuario no tiene ficha vinculada. Es un problema de
        // configuración, no un fallo transitorio, así que se dice tal cual
        // en vez de dejar la pantalla en blanco sin explicación.
        if (!esAdmin && err?.response?.status === 404) {
          setNombreFicha(null);
          setSinFichaVinculada(true);
        }
      })
      .finally(() => {
        if (!cancelado) setCargandoOperaciones(false);
      });
    return () => {
      cancelado = true;
    };
  }, [clienteId, esAdmin, user?.id]);

  // Cargar estructura cuando se selecciona cliente + minuta.
  // Reset previo de cantidadesPorDia y diasConCambios: el merge
  // selectivo de cargarEstructura preserva el state local de días
  // sin guardar — esto es deseable DENTRO de la misma combinación
  // (cliente, minuta), pero al cambiar de combinación queremos
  // empezar limpio para no mezclar cantidades de minutas distintas.
  // PASO 5: operacionId también dispara recarga (cambia el contrato
  // filtrado y el despacho destino).
  useEffect(() => {
    setCantidadesPorDia(new Map());
    setDiasConCambios(new Set());
    if (clienteId && minutaId && operacionId) {
      cargarEstructura();
      cargarEstadisticas();
    }
  }, [clienteId, minutaId, operacionId]);

  const cargarEstructura = async () => {
    if (!clienteId || !minutaId) return;

    try {
      setCargandoEstructura(true);
      const data = await PedidosNuevosAPI.obtenerEstructuraArmarPedido(
        clienteId,
        minutaId,
        operacionId ?? undefined
      );

      setEstructura(data);

      // Carta: inicializar día activo. Fix tanda2 T2-10 (2026-06-30):
      // antes se inicializaba SIEMPRE al primer día (lunes) — al
      // reabrir un borrador guardado, el usuario aterrizaba en lunes
      // aunque su borrador tuviera cantidades solo en, por ejemplo,
      // miércoles. Ahora priorizamos el primer día donde HAY cantidades
      // ya pedidas (celda.cantidadPedida > 0) o donde ya existe un
      // pedidoId persistido; sólo si no hay ninguno, caemos al día[0].
      if (data?.dias?.length) {
        const fechasValidas = data.dias.map((d) => d.fecha);
        if (!diaActivoCarta || !fechasValidas.includes(diaActivoCarta)) {
          const diasConDatos = new Set<string>();
          data.matriz.forEach((fila) => {
            fila.dias.forEach((celda) => {
              if (celda.pedidoId || (celda.cantidadPedida ?? 0) > 0) {
                diasConDatos.add(celda.fecha);
              }
            });
          });
          const primerDiaConDatos = data.dias.find((d) =>
            diasConDatos.has(d.fecha)
          );
          setDiaActivoCarta(
            (primerDiaConDatos?.fecha ?? data.dias[0].fecha)
          );
        }
      }

      // Merge selectivo de cantidades con el state local. Regla:
      // - Si el día tiene `pedidoId != null` (ya persistido en BD)
      //   el backend manda: usamos celda.cantidadPedida.
      // - Si el día tiene `pedidoId == null` (aún no guardado)
      //   preservamos el valor local que el usuario haya tipeado.
      //   Esto evita el bug donde guardar el día A pisaba el state
      //   no guardado del día B con cantidadPedida=0 del backend.
      // El reset al cambiar clienteId/minutaId (useEffect arriba)
      // garantiza que el merge no mezcle cantidades de minutas
      // distintas.
      // Fase 6 fix H-01 (post-F5): usamos flag de la RESPUESTA (data), no
      // la variable derivada `flagSegmentado` — esta última se calcula
      // del state `estructura` anterior y aún no refleja `data`. En un
      // reload duro (F5) el state previo era null y `flagSegmentado`
      // seguía false, así el merge saltaba la hidratación segmentada
      // y todos los inputs quedaban en 0 aunque el backend traía las
      // cantidades por segmento.
      const flagFromResponse =
        !!data?.operacion?.flagPedidoSegmentado;
      setCantidadesPorDia((prevLocal) => {
        const nuevasCantidades = new Map<string, Map<string, number>>();
        // Constante local para no depender del ID mágico en cada callback.
        const BEBIDA_ID = TIPO_PLATO_BEBIDA_ID;
        // Segmentos donde una fila puede aportar cantidad (para hidratar
        // las claves segmentadas). BEBIDA → transversal; duales → día+noche;
        // resto → su segmento base.
        const segmentosParaFila = (tp: {
          id: number;
          dual?: boolean;
          segmento?: 'dia' | 'noche' | 'transversal';
        }): Array<'dia' | 'noche' | 'transversal'> => {
          if (tp.id === BEBIDA_ID) return ['transversal'];
          if (tp.dual) return ['dia', 'noche'];
          if (tp.segmento === 'dia' || tp.segmento === 'noche') return [tp.segmento];
          return ['transversal'];
        };

        data.matriz.forEach((fila) => {
          fila.dias.forEach((celda) => {
            if (!celda.receta) return;
            if (!nuevasCantidades.has(celda.fecha)) {
              nuevasCantidades.set(celda.fecha, new Map());
            }
            const cKeyLegacy = claveCantidad(
              fila.tipoPlato.id,
              fila.tipoPlato.filaOrden
            );
            if (celda.pedidoId) {
              // Día persistido — backend canónico.
              nuevasCantidades
                .get(celda.fecha)!
                .set(cKeyLegacy, celda.cantidadPedida);
              // Fase 6 fix H-01: además, hidrata las claves SEGMENTADAS
              // usando cantidadPedidaPorSegmento si viene del backend
              // (siempre viene cuando pedidoId != null). Fallback: si
              // no llega, seguimos con la clave legacy.
              if (flagFromResponse) {
                const segs = segmentosParaFila(fila.tipoPlato);
                const porSeg = celda.cantidadPedidaPorSegmento;
                for (const seg of segs) {
                  const cKeySeg = claveCantidadSeg(
                    fila.tipoPlato.id,
                    fila.tipoPlato.filaOrden,
                    seg
                  );
                  const valSeg = porSeg?.[seg] ?? 0;
                  nuevasCantidades.get(celda.fecha)!.set(cKeySeg, valSeg);
                }
              }
            } else {
              // Día sin pedido — preservar local si existe;
              // fallback al backend (0 default) si no.
              const valorLocal = prevLocal.get(celda.fecha)?.get(cKeyLegacy);
              nuevasCantidades
                .get(celda.fecha)!
                .set(cKeyLegacy, valorLocal ?? celda.cantidadPedida);
              // Preservar también las claves segmentadas locales (usuario
              // tipeó cantidades y aún no guardó).
              if (flagFromResponse) {
                const segs = segmentosParaFila(fila.tipoPlato);
                for (const seg of segs) {
                  const cKeySeg = claveCantidadSeg(
                    fila.tipoPlato.id,
                    fila.tipoPlato.filaOrden,
                    seg
                  );
                  const localSeg = prevLocal.get(celda.fecha)?.get(cKeySeg);
                  nuevasCantidades
                    .get(celda.fecha)!
                    .set(cKeySeg, localSeg ?? 0);
                }
              }
            }
          });
        });
        return nuevasCantidades;
      });
    } catch (error: any) {
      console.error('Error cargando estructura:', error);
      toastError(
        error.response?.data?.error ||
          'Error al cargar la estructura de la minuta'
      );
    } finally {
      setCargandoEstructura(false);
    }
  };

  const cargarEstadisticas = async () => {
    if (!clienteId || !minutaId) return;

    try {
      const data = await PedidosNuevosAPI.obtenerEstadisticas(
        clienteId,
        minutaId
      );
      setEstadisticas(data);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  // Guardar borrador manualmente
  const guardarBorradorDia = useCallback(
    async (fecha: string) => {
      if (!clienteId || !minutaId) return;

      // Construir detalles ya agrupados por recetaId. El helper suma
      // cantidades cuando la misma receta aparece en varias celdas del
      // mismo día (caso raro: misma receta en dos tipos de plato).
      // El backend tiene UNIQUE(pedido_id, receta_id), por lo que debe
      // recibir UN detalle por receta.
      // En tipos combinados (COL.PRINCIPAL/CENA) cada agrupación trae
      // su `recetaGuarnicion` satélite; emitimos UN detalle extra por
      // la guarnición con la misma cantidad (ambos platos se cocinan).
      const agrupados = detallesAgrupadosDia(fecha);
      const detalles: Array<{
        recetaId: number;
        cantidad: number;
        segmento?: 'dia' | 'noche' | 'transversal';
        rol?: 'unico' | 'fondo' | 'guarnicion';
        tipoPlatoId?: number;
      }> = [];
      for (const item of agrupados) {
        detalles.push({
          recetaId: item.recetaId,
          cantidad: item.cantidad,
          segmento: item.segmento,
          rol: item.rol,
          tipoPlatoId: item.tipoPlatoId,
        });
        if (item.recetaGuarnicion) {
          // Espejo del rol en minutas_detalladas: la guarnición es
          // una fila aparte con la misma cantidad. Se persiste rol
          // para que sembrarPorDefecto NO la cuente. Hereda el mismo
          // tipoPlatoId del fondo (pertenece al mismo slot combinado).
          detalles.push({
            recetaId: item.recetaGuarnicion.id,
            cantidad: item.cantidad,
            segmento: item.segmento,
            rol: 'guarnicion',
            tipoPlatoId: item.tipoPlatoId,
          });
        }
      }

      // Protección: si todas las cantidades son cero, no llamar al backend.
      // El endpoint guardar-borrador hace DELETE de los detalles antes de
      // insertar; mandar array vacío borraría los detalles previamente
      // guardados sin reemplazo (data loss silencioso).
      if (detalles.length === 0) {
        toastError(
          'Ingresá al menos una cantidad mayor a 0 antes de guardar'
        );
        return;
      }

      try {
        setGuardandoDias((prev) => new Set(prev).add(fecha));

        await PedidosNuevosAPI.guardarBorrador({
          clienteId,
          operacionId: operacionId ?? undefined,
          minutaId,
          fecha,
          detalles,
        });

        // Marcar como guardado (sin cambios)
        setDiasConCambios((prev) => {
          const nuevo = new Set(prev);
          nuevo.delete(fecha);
          return nuevo;
        });

        toastSuccess('Pedido guardado correctamente');

        // Recargar estructura para obtener el pedidoId actualizado
        await cargarEstructura();

        // Actualizar estadísticas
        await cargarEstadisticas();
      } catch (error: any) {
        console.error('Error guardando borrador:', error);
        toastError(
          error.response?.data?.error || 'Error al guardar el borrador'
        );
      } finally {
        setGuardandoDias((prev) => {
          const nuevo = new Set(prev);
          nuevo.delete(fecha);
          return nuevo;
        });
      }
    },
    [clienteId, minutaId, operacionId, cantidadesPorDia, estructura]
  );

  // Handler para la vista día/noche. Las keys del Map incluyen el
  // segmento ('dia'|'noche'). Usado solo cuando flagSegmentado=true.
  // Clonamos tanto el outer como el inner Map para que React detecte
  // el cambio de identidad (el componente recibe el inner como prop;
  // si solo mutáramos el inner, prev === next y no re-renderiza).
  const handleCantidadChangeSegmentada = (
    fecha: string,
    tipoPlatoId: number,
    filaOrden: number,
    // Fase 6-bebidas (2026-07-01): también aceptamos 'transversal' para
    // las bebidas de la vista segmentada. El backend guarda ese literal
    // en pedido_detalles.segmento.
    segmento: 'dia' | 'noche' | 'transversal',
    nuevaCantidad: number
  ) => {
    setCantidadesPorDia((prev) => {
      const outer = new Map(prev);
      const inner = new Map(outer.get(fecha) ?? new Map());
      inner.set(
        claveCantidadSeg(tipoPlatoId, filaOrden, segmento),
        nuevaCantidad
      );
      outer.set(fecha, inner);
      return outer;
    });
    setDiasConCambios((prev) => {
      const nuevo = new Set(prev);
      nuevo.add(fecha);
      return nuevo;
    });
  };

  // Manejar cambio de cantidad. La key del state es la celda física
  // (tipoPlatoId + filaOrden), no el recetaId — así dos celdas con la
  // misma receta tienen valores independientes.
  const handleCantidadChange = (
    fecha: string,
    tipoPlatoId: number,
    filaOrden: number,
    nuevaCantidad: number
  ) => {
    setCantidadesPorDia((prev) => {
      const nuevasCantidades = new Map(prev);

      if (!nuevasCantidades.has(fecha)) {
        nuevasCantidades.set(fecha, new Map());
      }

      nuevasCantidades
        .get(fecha)!
        .set(claveCantidad(tipoPlatoId, filaOrden), nuevaCantidad);

      return nuevasCantidades;
    });

    // Marcar día como con cambios sin guardar
    setDiasConCambios((prev) => new Set(prev).add(fecha));
  };

  // Abrir modal de confirmación para un día
  const abrirModalConfirmarDia = (
    fecha: string,
    nombreDia: string,
    _pedidoId: number | null
  ) => {
    // Obtener detalles del día
    const dia = estructura?.dias.find((d) => d.fecha === fecha);

    if (!dia || !dia.pedidoId) {
      toastError('No se encontró el pedido para este día');
      return;
    }

    setModalConfirmarDia({
      isOpen: true,
      fecha,
      nombreDia,
      pedidoId: dia.pedidoId,
    });
  };

  // Confirmar pedido de un día
  const confirmarPedidoDia = async () => {
    if (!modalConfirmarDia.pedidoId) return;

    try {
      setConfirmandoPedido(true);

      await PedidosNuevosAPI.confirmarPedido(modalConfirmarDia.pedidoId);

      toastSuccess('Pedido confirmado exitosamente');

      // Cerrar modal
      setModalConfirmarDia({
        isOpen: false,
        fecha: '',
        nombreDia: '',
        pedidoId: null,
      });

      // Recargar estructura y estadísticas
      await cargarEstructura();
      await cargarEstadisticas();
      // Disparar refresh del banner de borradores: el pedido recién
      // confirmado debe desaparecer de la lista.
      setRefreshBanner((x) => x + 1);
    } catch (error: any) {
      console.error('Error confirmando pedido:', error);
      toastError(error.response?.data?.error || 'Error al confirmar el pedido');
    } finally {
      setConfirmandoPedido(false);
    }
  };

  // Seleccionar un borrador pendiente desde el banner.
  //
  // Caso típico: cambio de combinación cliente/minuta. setClienteId
  // + setMinutaId actualizan el state, y el useEffect existente
  // (líneas ~213-219) dispara cargarEstructura.
  //
  // Caso borde: el usuario ya tiene seleccionada la MISMA combinación
  // y hace click en otro borrador con mismo cliente + minuta. React
  // ve los valores iguales, no re-renderiza, el useEffect NO se
  // dispara y la grilla quedaría con los datos del borrador anterior.
  // Para ese caso forzamos la recarga manual.
  const seleccionarBorradorPendiente = useCallback(
    (cId: number, mId: number) => {
      const mismaCombinacion = cId === clienteId && mId === minutaId;

      // En ambos casos limpiamos el state derivado para que la grilla
      // muestre estado de "cargando" en vez de la combinación previa.
      setEstructura(null);
      setEstadisticas(null);

      if (mismaCombinacion) {
        // React no dispararía el useEffect — recargamos a mano.
        // Reset previo idéntico al que hace el useEffect (ver línea
        // ~290) para mantener el invariante: el merge de
        // cargarEstructura preserva sólo cantidades de ESTA
        // combinación.
        setCantidadesPorDia(new Map());
        setDiasConCambios(new Set());
        cargarEstructura();
        cargarEstadisticas();
      } else {
        // Cambio real → setState dispara useEffect que hace el reset
        // y la carga.
        setClienteId(cId);
        setMinutaId(mId);
      }
    },
    [clienteId, minutaId]
  );

  // Modificar pedido confirmado (vuelve a borrador)
  const modificarPedidoConfirmado = async (
    pedidoId: number,
    nombreDia: string
  ) => {
    setModalConfirm({
      isOpen: true,
      title: 'Modificar Pedido',
      message: `¿Está seguro de modificar el pedido del ${nombreDia}?\n\nEl pedido volverá a estado "Borrador" y deberá confirmarlo nuevamente después de hacer los cambios.`,
      type: 'warning',
      onConfirm: async () => {
        try {
          await PedidosNuevosAPI.modificarPedido(pedidoId);

          toastSuccess('Pedido vuelto a estado borrador');

          // Cerrar modal
          setModalConfirm({
            ...modalConfirm,
            isOpen: false,
          });

          // Recargar estructura y estadísticas
          await cargarEstructura();
          await cargarEstadisticas();
        } catch (error: any) {
          console.error('Error modificando pedido:', error);
          toastError(
            error.response?.data?.error || 'Error al modificar el pedido'
          );
        }
      },
    });
  };

  // Renderizado condicional según el estado de carga
  if (cargandoEstructura) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            Cargando estructura de la minuta...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => window.history.back()}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>

          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <ChefHat className="w-7 h-7" />
              Armar Pedido
              <AyudaContextual seccion="armar-pedido" />
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Seleccione cliente y minuta para comenzar
            </p>
          </div>
        </div>

        {/* Banner "Pedidos por confirmar" — visible sólo para
            rol admin/cliente, oculto para chef/cocina. Falla
            silenciosa: si el endpoint falla o no hay items, no
            se muestra nada (no agrega ruido). */}
        <BannerBorradoresPendientes
          onSeleccionar={seleccionarBorradorPendiente}
          refreshKey={refreshBanner}
        />

        {/* Selectores */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow space-y-4">
          {/* Indicador de modo (para admin) */}
          {esAdmin && clienteId && user?.id !== clienteId && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg p-3 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Modo Transcripción Activo
                </p>
                <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                  Está creando un pedido en nombre del cliente seleccionado.
                  Este pedido se marcará como "Transcrito".
                </p>
              </div>
            </div>
          )}

          {/* Cliente actual (para clientes normales) */}
          {!esAdmin && user && !sinFichaVinculada && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Cliente
              </label>
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {nombreFicha ?? user.nombre ?? user.email}
                </p>
              </div>
            </div>
          )}

          {/* Usuario sin ficha de cliente asociada: no puede armar pedidos */}
          {!esAdmin && sinFichaVinculada && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg p-3 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Tu usuario no tiene una ficha de cliente asociada
                </p>
                <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                  Sin esa vinculación no es posible armar un pedido. Pedile a un
                  administrador que asocie tu usuario a la ficha de tu empresa.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Selector de Cliente (solo para admin) */}
            {esAdmin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Seleccionar Cliente *
                </label>
                <select
                  value={clienteId || ''}
                  onChange={(e) => {
                    setClienteId(parseInt(e.target.value) || null);
                    setMinutaId(null); // Reset minuta al cambiar cliente
                    setEstructura(null);
                    setEstadisticas(null);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- Seleccione un cliente --</option>
                  {clientes.map((cliente) => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.razon_social || cliente.nombre} - {cliente.rut}
                    </option>
                  ))}
                </select>
                {!clienteId && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Primero seleccione el cliente para quien creará el pedido
                  </p>
                )}
              </div>
            )}

            {/* Selector de Minuta */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Minuta Vigente *
              </label>
              <select
                value={minutaId || ''}
                onChange={(e) => setMinutaId(parseInt(e.target.value) || null)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={esAdmin && !clienteId}
              >
                <option value="">-- Seleccione una minuta --</option>
                {minutasActivas.map((minuta) => (
                  <option key={minuta.id} value={minuta.id}>
                    {formatearNombreMinuta(minuta.nombre)}
                  </option>
                ))}
                {/* Si el usuario buscó históricas y eligió una, la
                    incluimos acá para que el <select> pueda mostrarla
                    seleccionada con su nombre. */}
                {historicasHook.resultados
                  .filter((m: any) => m.id === minutaId)
                  .map((minuta: any) => (
                    <option key={`hist-${minuta.id}`} value={minuta.id}>
                      {formatearNombreMinuta(minuta.nombre)} (histórica)
                    </option>
                  ))}
              </select>
              {esAdmin && !clienteId && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Seleccione primero un cliente
                </p>
              )}
              {minutasActivas.length === 0 && (!esAdmin || clienteId) && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  No hay minutas disponibles para los próximos 30 días.
                  Probá el buscador de minutas anteriores abajo.
                </p>
              )}
            </div>
          </div>

          {/* Selector de operación (multi-sede) — visible sólo cuando el
              cliente tiene ≥ 2 operaciones activas. Con 1 sola operación
              el dropdown se omite y el backend usa la principal por
              defecto. TD-034 cerrado en PASO 5. */}
          {clienteId && operacionesCliente.length >= 2 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Operación / Faena *
              </label>
              <select
                value={operacionId ?? ''}
                onChange={(e) =>
                  setOperacionId(parseInt(e.target.value) || null)
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                disabled={cargandoOperaciones}
              >
                {operacionesCliente.map((op) => (
                  <option key={op.id} value={op.id}>
                    {op.nombre}
                    {op.es_principal ? ' (Principal)' : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Cambiar la operación recarga el menú filtrado por su
                contrato y dirige el despacho a su dirección.
              </p>
            </div>
          )}
          {clienteId &&
            !cargandoOperaciones &&
            operacionesCliente.length === 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Este cliente no tiene operaciones activas. Configure una
                en Clientes antes de armar el pedido.
              </p>
            )}

          {/* Acordeón compartido con la pantalla Minutas. */}
          <div className="mt-4">
            <BuscadorMinutasHistoricasAcordeon
              hook={historicasHook}
              idsExcluir={new Set(minutasActivas.map((m: any) => m.id))}
              renderResultados={(resultados) => (
                <ul className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
                  {resultados.map((minuta: any) => {
                    const seleccionada = minuta.id === minutaId;
                    return (
                      <li key={minuta.id}>
                        <button
                          type="button"
                          onClick={() => setMinutaId(minuta.id)}
                          disabled={esAdmin && !clienteId}
                          className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                            seleccionada
                              ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {formatearNombreMinuta(minuta.nombre)}
                          {seleccionada && (
                            <span className="ml-2 text-xs">✓ seleccionada</span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            />
          </div>
        </div>
      </div>

      {/* Estadísticas */}
      {estadisticas && estructura && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex items-center gap-4">
            <CheckCircle className="w-10 h-10 text-green-500" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Días completos
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {estadisticas.diasConfirmados}/{estadisticas.totalDiasMinuta}
              </p>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex items-center gap-4">
            <Clock className="w-10 h-10 text-yellow-500" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Pendientes
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {estadisticas.diasPendientes} días
              </p>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex items-center gap-4">
            <AlertCircle className="w-10 h-10 text-red-500" />
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Sin cantidad
              </p>
              <p
                className="text-2xl font-bold text-gray-900 dark:text-white"
                data-testid="widget-sin-cantidad"
              >
                {recetasSinCantidadReactivo} recetas
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Toggle vista carta / semanal (rediseño 2026-06-15) */}
      {estructura && (
        <div className="mb-4 flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg shadow px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Vista:
            </span>
            <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
              <button
                onClick={() => setVistaMode('carta')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  vistaMode === 'carta'
                    ? 'bg-[#29ABE2] text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
                aria-pressed={vistaMode === 'carta'}
              >
                🍽️ Carta
              </button>
              <button
                onClick={() => setVistaMode('semanal')}
                disabled={!isDesktop}
                title={
                  !isDesktop
                    ? 'La grilla semanal requiere pantalla amplia — usa la vista Carta en móvil'
                    : undefined
                }
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  vistaMode === 'semanal' && isDesktop
                    ? 'bg-[#29ABE2] text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
                aria-pressed={vistaMode === 'semanal'}
              >
                📅 Semanal
              </button>
            </div>
            {!isDesktop && vistaMode === 'semanal' && (
              <span className="text-[11px] text-orange-600 dark:text-orange-400">
                Móvil: mostrando Carta
              </span>
            )}
          </div>
        </div>
      )}

      {/* Vista SEGMENTADA día/noche — solo cuando la operación tiene
          flag_pedido_segmentado=1. Cuando está apagado (estado actual
          de todas las operaciones) este componente NO se renderiza y
          el flujo cae en la carta/grilla semanal como hoy. */}
      {estructura && flagSegmentado && diaActivoCarta && (() => {
        const diaActivoInfo = estructura.dias.find(
          (d) => d.fecha === diaActivoCarta
        );
        return (
          <VistaSegmentadaDiaNoche
            estructura={estructura}
            diaActivo={diaActivoCarta}
            cantidadesDia={cantidadesPorDia.get(diaActivoCarta) ?? new Map()}
            onCantidadChange={handleCantidadChangeSegmentada}
            onGuardar={guardarBorradorDia}
            guardando={guardandoDias.has(diaActivoCarta)}
            tieneCambios={diasConCambios.has(diaActivoCarta)}
            onConfirmar={abrirModalConfirmarDia}
            estadoDiaActivo={diaActivoInfo?.estado}
            pedidoIdDiaActivo={diaActivoInfo?.pedidoId}
            nombreDiaActivo={diaActivoInfo?.nombre}
            bebidaTipoPlatoId={TIPO_PLATO_BEBIDA_ID}
            dias={estructura.dias}
            onCambiarDia={setDiaActivoCarta}
            totalDelDia={totalPorDia.get(diaActivoCarta) ?? 0}
          />
        );
      })()}

      {/* Vista CARTA DE MENÚ — por día */}
      {estructura && !flagSegmentado && vistaEfectiva === 'carta' && diaActivoCarta && (
        <CartaDeMenu
          estructura={estructura}
          diaActivo={diaActivoCarta}
          setDiaActivo={setDiaActivoCarta}
          cantidadesPorDia={cantidadesPorDia}
          handleCantidadChange={handleCantidadChange}
          guardarBorradorDia={guardarBorradorDia}
          abrirModalConfirmarDia={abrirModalConfirmarDia}
          modificarPedidoConfirmado={modificarPedidoConfirmado}
          guardandoDias={guardandoDias}
          diasConCambios={diasConCambios}
          claveCantidad={claveCantidad}
          bebidaTipoPlatoId={TIPO_PLATO_BEBIDA_ID}
          totalPorDia={totalPorDia}
        />
      )}

      {/* Vista SEMANAL — grilla categorias x dias (2026-06-15) */}
      {estructura && !flagSegmentado && vistaEfectiva === 'semanal' && (
        <GrillaSemanal
          estructura={estructura}
          cantidadesPorDia={cantidadesPorDia}
          handleCantidadChange={handleCantidadChange}
          guardarBorradorDia={guardarBorradorDia}
          abrirModalConfirmarDia={abrirModalConfirmarDia}
          modificarPedidoConfirmado={modificarPedidoConfirmado}
          guardandoDias={guardandoDias}
          diasConCambios={diasConCambios}
          claveCantidad={claveCantidad}
          bebidaTipoPlatoId={TIPO_PLATO_BEBIDA_ID}
          totalPorDia={totalPorDia}
        />
      )}

      {/* Modal de confirmación de día */}
      {modalConfirmarDia.isOpen && estructura && (
        <ConfirmarPedidoDiaModal
          isOpen={modalConfirmarDia.isOpen}
          fecha={modalConfirmarDia.fecha}
          nombreDia={modalConfirmarDia.nombreDia}
          detalles={detallesAgrupadosDia(modalConfirmarDia.fecha).map(
            (item) => ({
              recetaNombre: item.nombre,
              cantidad: item.cantidad,
            })
          )}
          onConfirm={confirmarPedidoDia}
          onCancel={() =>
            setModalConfirmarDia({
              isOpen: false,
              fecha: '',
              nombreDia: '',
              pedidoId: null,
            })
          }
          isLoading={confirmandoPedido}
        />
      )}

      {/* Modal de confirmación genérico */}
      <ConfirmModal
        isOpen={modalConfirm.isOpen}
        onClose={() => setModalConfirm({ ...modalConfirm, isOpen: false })}
        onConfirm={modalConfirm.onConfirm}
        title={modalConfirm.title}
        message={modalConfirm.message}
        type={modalConfirm.type}
      />
    </div>
  );
};

export default ArmarPedido;

// =====================================================================
// CartaDeMenu — vista "carta de menú" del rediseño 2026-06-12
// =====================================================================
//
// SOLO presentación. Recibe la misma `estructura` que la matriz clásica
// y reutiliza los handlers de cantidad / guardar / confirmar del padre.
// Por día activo:
//   - Tabs de días arriba con su estado y total porciones / bebidas.
//   - Sección "Comida": cards por fila tipo_plato != bebida (id 11).
//     Cada card: nombre + descripción + badge "Plato compuesto" (cuando
//     `recetaGuarnicion` está presente) + label Porciones + input number.
//   - Sección "Bebidas": lista vertical, una fila por bebida, total al
//     final ("Total bebidas: N").
//   - Resumen vivo + botones Guardar / Confirmar.
//   - Validación bebidas ≤ comida: deshabilita Confirmar si excede.
//
// Branding NexGen: cyan #29ABE2 (acentos) + naranja #F7941D (badges /
// hover). Layout responsive con grid 1 col en mobile, 2 col en md+.

/**
 * Mapeo nombre tipo plato → emoji (rediseño 2026-06-13).
 * Coincidencia exacta por nombre canónico en mayúsculas; si no hay
 * match exacto, busca substring. Cualquier tipo no listado cae al
 * fallback 🍴. Si el dominio agrega un tipo nuevo, este mapeo lo cubre
 * con el fallback sin romper la pantalla.
 */
const ICONO_POR_TIPO_PLATO: Record<string, string> = {
  SOPA: '🍲',
  'COLACIÓN PRINCIPAL': '🍽️',
  'COLACIÓN PRINCIPAL 2': '🍽️',
  'COLACIÓN PRINCIPAL 3': '🍽️',
  HIPOCALÓRICO: '🥗',
  'DIETA BLANDA': '🍵',
  'ALMUERZO SIN GLUTEN': '🌾',
  CENA: '🌙',
  ENSALADA: '🥗',
  POSTRE: '🍰',
  DESAYUNO: '☕',
  SÁNDWICH: '🥪',
  SANDWICH: '🥪',
  ONCE: '☕',
  COLACIÓN: '🥡',
  COLACION: '🥡',
  BEBIDA: '🥤',
  BEBIDAS: '🥤',
  EXTRA: '✨',
  FONDO: '🍖',
  GUARNICIÓN: '🍚',
};

const iconoSeccion = (nombre: string): string => {
  const key = nombre.trim().toUpperCase();
  if (ICONO_POR_TIPO_PLATO[key]) return ICONO_POR_TIPO_PLATO[key];
  for (const [k, v] of Object.entries(ICONO_POR_TIPO_PLATO)) {
    if (key.includes(k)) return v;
  }
  return '🍴';
};

interface CartaDeMenuProps {
  estructura: EstructuraArmarPedido;
  diaActivo: string;
  setDiaActivo: (fecha: string) => void;
  cantidadesPorDia: Map<string, Map<string, number>>;
  handleCantidadChange: (
    fecha: string,
    tipoPlatoId: number,
    filaOrden: number,
    cantidad: number
  ) => void;
  guardarBorradorDia: (fecha: string) => void;
  abrirModalConfirmarDia: (
    fecha: string,
    nombreDia: string,
    pedidoId: number | null
  ) => void;
  modificarPedidoConfirmado: (pedidoId: number, nombreDia: string) => void;
  guardandoDias: Set<string>;
  diasConCambios: Set<string>;
  claveCantidad: (tipoPlatoId: number, filaOrden: number) => string;
  bebidaTipoPlatoId: number;
  /** #5 (2026-07-01): total de porciones por día calculado en el padre
   *  (útil para gatear el botón Confirmar — no permite confirmar vacío). */
  totalPorDia: Map<string, number>;
}

const CartaDeMenu: React.FC<CartaDeMenuProps> = ({
  estructura,
  diaActivo,
  setDiaActivo,
  cantidadesPorDia,
  handleCantidadChange,
  guardarBorradorDia,
  abrirModalConfirmarDia,
  modificarPedidoConfirmado,
  guardandoDias,
  diasConCambios,
  claveCantidad,
  totalPorDia,
  bebidaTipoPlatoId,
}) => {
  const dia = estructura.dias.find((d) => d.fecha === diaActivo);

  // Filas que tienen una receta asignada al día activo, separadas por
  // comida / bebida. Cada fila viaja con su cantidad actual y un setter.
  const filasComidaDia = estructura.matriz.filter((f) => {
    if (f.tipoPlato.id === bebidaTipoPlatoId) return false;
    const celda = f.dias.find((d) => d.fecha === diaActivo);
    return celda?.receta !== null && celda?.receta !== undefined;
  });
  const filasBebidaDia = estructura.matriz.filter((f) => {
    if (f.tipoPlato.id !== bebidaTipoPlatoId) return false;
    const celda = f.dias.find((d) => d.fecha === diaActivo);
    return celda?.receta !== null && celda?.receta !== undefined;
  });

  const getCantidad = (tipoPlatoId: number, filaOrden: number): number => {
    return (
      cantidadesPorDia.get(diaActivo)?.get(claveCantidad(tipoPlatoId, filaOrden)) ??
      0
    );
  };

  const totalComidaDia = filasComidaDia.reduce(
    (acc, f) => acc + getCantidad(f.tipoPlato.id, f.tipoPlato.filaOrden),
    0
  );
  const totalBebidasDia = filasBebidaDia.reduce(
    (acc, f) => acc + getCantidad(f.tipoPlato.id, f.tipoPlato.filaOrden),
    0
  );
  const excedeBebidas = totalBebidasDia > totalComidaDia;

  // Agrupar filas comida por nombre del tipo de plato (sección).
  const seccionesComida = new Map<string, typeof filasComidaDia>();
  for (const f of filasComidaDia) {
    const key = f.tipoPlato.nombre;
    if (!seccionesComida.has(key)) seccionesComida.set(key, []);
    seccionesComida.get(key)!.push(f);
  }

  const editable = dia?.estado === 'borrador';

  return (
    <div className="space-y-4">
      {/* Tabs de días */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 overflow-x-auto">
        <div className="flex gap-2 min-w-max">
          {estructura.dias.map((d) => {
            const activo = d.fecha === diaActivo;
            return (
              <button
                key={d.fecha}
                onClick={() => setDiaActivo(d.fecha)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  activo
                    ? 'bg-[#29ABE2] text-white shadow-sm'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <div className="text-xs opacity-80">{d.fecha}</div>
                <div className="font-semibold">{d.nombre}</div>
                <div
                  className={`mt-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
                    d.estado === 'borrador'
                      ? 'bg-yellow-100 text-yellow-800'
                      : d.estado === 'solicitado'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {d.estado}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {dia && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Columna principal: secciones */}
          <div className="lg:col-span-2 space-y-6">
            {/* Comida */}
            {seccionesComida.size === 0 && filasBebidaDia.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500">
                Sin platos asignados para este día.
              </div>
            ) : null}

            {[...seccionesComida.entries()].map(([nombreSeccion, filas]) => (
              <section
                key={nombreSeccion}
                className="bg-white dark:bg-gray-800 rounded-lg shadow"
              >
                <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
                  <span className="inline-block w-1 h-6 bg-[#F7941D] rounded-full" />
                  <span className="text-xl leading-none" aria-hidden="true">
                    {iconoSeccion(nombreSeccion)}
                  </span>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                    {nombreSeccion}
                  </h3>
                </header>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filas.map((f) => {
                    const celda = f.dias.find((d) => d.fecha === diaActivo)!;
                    if (!celda.receta) return null;
                    const cant = getCantidad(
                      f.tipoPlato.id,
                      f.tipoPlato.filaOrden
                    );
                    const compuesto = !!celda.receta.recetaGuarnicion;
                    // El backend ya unifica el nombre como "<Fondo> con
                    // <Guarnición>" cuando hay guarnición. Si receta.nombre
                    // ya contiene "con <guarnicion>", usar tal cual; si
                    // no, concatenar (defensa para distintas versiones del
                    // endpoint).
                    const guarNombre = celda.receta.recetaGuarnicion?.nombre || '';
                    const yaConcatenado =
                      compuesto && guarNombre &&
                      celda.receta.nombre.toLowerCase().includes(
                        ' con ' + guarNombre.toLowerCase()
                      );
                    const nombrePlato =
                      !compuesto || yaConcatenado
                        ? celda.receta.nombre
                        : `${celda.receta.nombre} con ${guarNombre}`;
                    // descripcion = backend ya envía `descripcion_menu`
                    // como el campo `descripcion` (ver pedidosNuevos.ts).
                    // Si está vacío/NULL no se muestra subtítulo — la
                    // antigua nota técnica + "Cargada DD-MM-YYYY" +
                    // tag "capacitacion" quedan FUERA de la carta del
                    // cliente por diseño.
                    const descripcionMenu =
                      (celda.receta.descripcion || '').trim();
                    return (
                      <article
                        key={`${f.tipoPlato.id}-${f.tipoPlato.filaOrden}`}
                        className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 flex flex-col gap-3 hover:border-[#29ABE2] transition-colors"
                      >
                        {/* Slot para foto futura del plato. Mientras
                            `imagen_url` no exista en el dominio queda
                            como placeholder con aspect-ratio para que
                            la card tenga el espacio reservado y la
                            cuadrícula no salte cuando se agregue. */}
                        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                        {(celda.receta as any).imagen_url ? (
                          <img
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            src={(celda.receta as any).imagen_url as string}
                            alt={nombrePlato}
                            className="w-full aspect-[16/9] object-cover rounded-md bg-gray-100"
                            loading="lazy"
                          />
                        ) : (
                          <div
                            className="w-full aspect-[16/9] rounded-md bg-gray-100 dark:bg-gray-700/40 flex items-center justify-center text-3xl select-none"
                            aria-hidden="true"
                            title="Foto del plato (próximamente)"
                          >
                            <span className="opacity-60">
                              {iconoSeccion(nombreSeccion)}
                            </span>
                          </div>
                        )}
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                              {nombrePlato}
                            </h4>
                            {descripcionMenu && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                {descripcionMenu}
                              </p>
                            )}
                          </div>
                          {compuesto && (
                            <span className="inline-block text-[10px] uppercase tracking-wider bg-[#F7941D]/15 text-[#F7941D] px-2 py-1 rounded font-semibold whitespace-nowrap">
                              Plato compuesto
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                            Porciones
                          </label>
                          <input
                            type="number"
                            inputMode="numeric"
                            min={0}
                            value={cant === 0 ? '' : cant}
                            disabled={!editable}
                            onChange={(e) => {
                              const v = parseInt(e.target.value || '0', 10);
                              handleCantidadChange(
                                diaActivo,
                                f.tipoPlato.id,
                                f.tipoPlato.filaOrden,
                                isNaN(v) || v < 0 ? 0 : v
                              );
                            }}
                            className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-right font-semibold focus:ring-2 focus:ring-[#29ABE2] focus:border-[#29ABE2] disabled:opacity-60 disabled:cursor-not-allowed"
                            placeholder="0"
                          />
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}

            {/* Bebidas */}
            {filasBebidaDia.length > 0 && (
              <section className="bg-white dark:bg-gray-800 rounded-lg shadow">
                <header className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
                  <span className="inline-block w-1 h-6 bg-[#29ABE2] rounded-full" />
                  <span className="text-xl leading-none" aria-hidden="true">
                    {iconoSeccion('BEBIDA')}
                  </span>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                    Bebidas
                  </h3>
                </header>
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filasBebidaDia.map((f) => {
                    const celda = f.dias.find((d) => d.fecha === diaActivo)!;
                    if (!celda.receta) return null;
                    const cant = getCantidad(
                      f.tipoPlato.id,
                      f.tipoPlato.filaOrden
                    );
                    return (
                      <li
                        key={`${f.tipoPlato.id}-${f.tipoPlato.filaOrden}`}
                        className="px-5 py-3 flex items-center justify-between gap-4"
                      >
                        <span className="text-sm text-gray-900 dark:text-white flex-1 min-w-0 truncate">
                          {celda.receta.nombre}
                        </span>
                        <input
                          type="number"
                          inputMode="numeric"
                          min={0}
                          value={cant === 0 ? '' : cant}
                          disabled={!editable}
                          onChange={(e) => {
                            const v = parseInt(e.target.value || '0', 10);
                            handleCantidadChange(
                              diaActivo,
                              f.tipoPlato.id,
                              f.tipoPlato.filaOrden,
                              isNaN(v) || v < 0 ? 0 : v
                            );
                          }}
                          className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-right font-semibold focus:ring-2 focus:ring-[#29ABE2] focus:border-[#29ABE2] disabled:opacity-60 disabled:cursor-not-allowed"
                          placeholder="0"
                        />
                      </li>
                    );
                  })}
                </ul>
                <footer className="px-5 py-3 bg-gray-50 dark:bg-gray-900/30 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Total bebidas
                  </span>
                  <span className="text-base font-bold text-[#29ABE2]">
                    {totalBebidasDia}
                  </span>
                </footer>
              </section>
            )}
          </div>

          {/* Resumen lateral (sticky en lg+) */}
          <aside className="lg:sticky lg:top-4 self-start bg-white dark:bg-gray-800 rounded-lg shadow p-5 space-y-4">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-3">
              Resumen del día
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-700 dark:text-gray-300">
                  Comida — porciones
                </span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {totalComidaDia}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-700 dark:text-gray-300">
                  Bebidas
                </span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {totalBebidasDia}
                </span>
              </div>
            </div>

            {excedeBebidas && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 text-xs px-3 py-2 rounded">
                Las bebidas ({totalBebidasDia}) no pueden superar la cantidad
                de comida ({totalComidaDia}).
              </div>
            )}

            {dia.estado === 'borrador' ? (
              <div className="flex flex-col gap-2">
                {diasConCambios.has(dia.fecha) || !dia.pedidoId ? (
                  <button
                    onClick={() => guardarBorradorDia(dia.fecha)}
                    disabled={guardandoDias.has(dia.fecha) || excedeBebidas}
                    title={
                      excedeBebidas
                        ? 'Corrige las bebidas antes de guardar (no pueden superar la comida).'
                        : ''
                    }
                    className="w-full px-4 py-2 bg-[#29ABE2] hover:bg-[#1f8fbf] text-white rounded-lg text-sm font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {guardandoDias.has(dia.fecha)
                      ? 'Guardando…'
                      : 'Guardar borrador'}
                  </button>
                ) : null}
                <button
                  onClick={() =>
                    abrirModalConfirmarDia(dia.fecha, dia.nombre, dia.pedidoId)
                  }
                  disabled={
                    !dia.pedidoId ||
                    excedeBebidas ||
                    diasConCambios.has(dia.fecha) ||
                    (totalPorDia.get(dia.fecha) ?? 0) === 0
                  }
                  className="w-full px-4 py-2 bg-[#F7941D] hover:bg-[#d97d12] text-white rounded-lg text-sm font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  title={
                    (totalPorDia.get(dia.fecha) ?? 0) === 0
                      ? 'Ingresa al menos 1 porción antes de confirmar.'
                      : diasConCambios.has(dia.fecha)
                        ? 'Guardá los cambios antes de confirmar'
                        : excedeBebidas
                          ? 'Bebidas exceden comida'
                          : ''
                  }
                >
                  Confirmar pedido del día
                </button>
              </div>
            ) : dia.estado === 'solicitado' ? (
              <button
                onClick={() =>
                  dia.pedidoId &&
                  modificarPedidoConfirmado(dia.pedidoId, dia.nombre)
                }
                className="w-full px-4 py-2 bg-[#F7941D]/15 hover:bg-[#F7941D]/25 text-[#F7941D] rounded-lg text-sm font-semibold transition-colors"
              >
                Modificar pedido confirmado
              </button>
            ) : (
              <p className="text-xs text-gray-500 text-center">
                Estado: {dia.estado}
              </p>
            )}
          </aside>
        </div>
      )}
    </div>
  );
};
